import { enemyByKind } from "./enemy-defs.js";
/**
 * Screen-space sprite hit-testing for enemies: the input layer's "body
 * radius". Where actions.ts resolves a click to the enemy only when it lands
 * ON the enemy's foot tile (ENEMY_CLICK_RADIUS = 0 — the deliberate
 * tighten-the-clickbox rule), a billboard can be far larger than its tile: the
 * 256 px dragon boss spans ~2.7 tiles wide and ~5 tile-heights tall, so
 * clicking the visible body (wings, neck, tail) must hit the creature, not
 * walk underneath it. This is the geometrically honest version of a radius:
 * hit-test the exact sprite rect the Renderer drew (atlas family dims/anchor
 * at the interpolated render position, in screen space), picking the
 * painter-frontmost enemy when several bodies overlap.
 *
 * Pure over (assets, observation, render positions, screen point, camera) —
 * no DOM, no wall-clock. The Viewport calls this on pointer-down, right-click,
 * and hover. Left-click on the floor keeps the screen point through body
 * picking: a visible body wins over an item on its foot tile, while bare
 * ground on that tile still takes the item or walks. Right-click continues
 * collecting both ground and body targets for the menu.
 *
 * Tradeoff (recorded in actions.ts's ENEMY_CLICK_RADIUS history): the atlas
 * frame is reduced to a conservative ellipse, shrunk by ENEMY_HIT_INSET;
 * a small second-chance margin catches near misses without making the frame's
 * transparent corners clickable. A per-frame alpha-mask hit-test can replace
 * this without changing the public resolver. Dead
 * enemies and missing atlas families return null — tile behaviour (walk /
 * corpse) unchanged.
 */

import type { Observation, ObservedEnemy, ObservedPlayer } from "@pd/shared";
import { HALF_H, HALF_W, tileToScreen } from "./iso.js";
import { painterOrder } from "./iso.js";
import type { HeroAssets } from "./hero.js";
import { enemyKey, playerKey } from "./interpolate.js";
import type { Vec2 } from "./actions.js";

/** A screen-space axis-aligned rect (viewport coords, CSS px). */
export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Shrink the frame rect by this fraction of each dimension, so the
 *  transparent margins of the 96 px contract (and the big 256 px boss frames)
 *  don't register as body. 6% ≈ 6 px at the base 96 px scale, a fingertip-sized
 *  sliver — the body is what's left. */
export const ENEMY_HIT_INSET = 0.06;

/** Screen-space body rect for any atlas billboard. Shared by floor enemies
 * and hub players so input follows the same feet/anchor math as rendering. */
export function spriteRect(
  dims: readonly [number, number],
  anchor: readonly [number, number],
  pos: Vec2,
  u: number,
  camX: number,
  camY: number,
  inset = ENEMY_HIT_INSET,
): Rect {
  const p = tileToScreen(pos.x, pos.y, u, camX, camY);
  const feet = p.y + HALF_H * u - (pos.height ?? 0) * u;
  const w = dims[0] * u;
  const h = dims[1] * u;
  const ix = w * inset;
  const iy = h * inset;
  return {
    left: p.x - anchor[0] * u + ix,
    right: p.x - anchor[0] * u + w - ix,
    top: feet - anchor[1] * u + iy,
    bottom: feet - anchor[1] * u + h - iy,
  };
}

export function spriteRectContains(r: Rect, sx: number, sy: number): boolean {
  return contains(r, sx, sy);
}

function frameContains(r: Rect, sx: number, sy: number): boolean {
  return sx >= r.left && sx <= r.right && sy >= r.top && sy <= r.bottom;
}

function contains(r: Rect, sx: number, sy: number): boolean {
  // The atlas frame is a transparent billboard. An AABB made the empty
  // corners between two creatures clickable, so a click in the gap picked
  // whichever rectangle happened to be visited first. An ellipse is the
  // conservative body silhouette used until a per-frame alpha mask is
  // available, and agrees with the visible central mass rather than the
  // transparent frame corners.
  const cx = (r.left + r.right) / 2;
  const cy = (r.top + r.bottom) / 2;
  const rx = (r.right - r.left) / 2;
  const ry = (r.bottom - r.top) / 2;
  if (rx <= 0 || ry <= 0) return false;
  const dx = (sx - cx) / rx;
  const dy = (sy - cy) / ry;
  return dx * dx + dy * dy <= 1;
}

/** A small second-chance margin outside an enemy's visible-body ellipse.
 * Exact body hits still win when bodies overlap; this margin only catches
 * near misses. CSS pixels keep it consistent across viewport zoom levels. */
export const BODY_CLICK_MARGIN = 10;

function containsWithMargin(r: Rect, sx: number, sy: number, margin: number): boolean {
  const cx = (r.left + r.right) / 2;
  const cy = (r.top + r.bottom) / 2;
  const rx = (r.right - r.left) / 2 + margin;
  const ry = (r.bottom - r.top) / 2 + margin;
  if (rx <= 0 || ry <= 0) return false;
  const dx = (sx - cx) / rx;
  const dy = (sy - cy) / ry;
  return dx * dx + dy * dy <= 1;
}

/** Find the nearest floor object's tile inside a lightly expanded projected
 * tile diamond. This catches a click just outside the tile containing a small
 * key, stone, drop, sack or portal without making its whole neighbouring tile
 * an interaction target. Callers check the raw tile first, so a nearby object
 * never steals a click from an object directly under the cursor. */
export function groundObjectTileAtScreenPoint(
  obs: Observation,
  sx: number,
  sy: number,
  u: number,
  camX: number,
  camY: number,
): Vec2 | null {
  const margin = 10;
  const best: { tile: Vec2 | null; score: number } = { tile: null, score: Infinity };
  const consider = (x: number, y: number): void => {
    const p = tileToScreen(x, y, u, camX, camY);
    const score = Math.abs(sx - p.x) / (HALF_W * u + margin)
      + Math.abs(sy - (p.y + HALF_H * u)) / (HALF_H * u + margin);
    if (score > 1) return;
    const tile = best.tile;
    if (!tile || score < best.score || (score === best.score && (y < tile.y || (y === tile.y && x < tile.x)))) {
      best.tile = { x, y };
      best.score = score;
    }
  };
  for (const key of obs.keys) consider(key.x, key.y);
  for (const stone of obs.stones) if (stone.state === "floor" && stone.x !== undefined && stone.y !== undefined) consider(stone.x, stone.y);
  for (const drop of obs.drops) consider(drop.x, drop.y);
  if (obs.sack) consider(obs.sack.x, obs.sack.y);
  if (obs.boss?.state === "portal") consider(obs.boss.x, obs.boss.y);
  if (obs.returnPortal) consider(obs.returnPortal.x, obs.returnPortal.y);
  return best.tile;
}

/** Door art is taller than its floor tile. Keep the visible slab clickable
 * after narrowing the coarse tile rule: hit the inset sprite ellipse, not
 * the surrounding 3×3 block of floor. Missing art falls back to tile input. */
export function doorTileAtScreenPoint(
  assets: HeroAssets | null,
  obs: Observation,
  sx: number,
  sy: number,
  u: number,
  camX: number,
  camY: number,
): Vec2 | null {
  let best: { x: number; y: number; id: number; score: number } | null = null;
  for (const door of obs.doors) {
    const frame = assets?.atlas.props?.[door.open ? "door-open" : "door-closed"];
    if (!frame) continue;
    const r = spriteRect(frame.dims, frame.anchor, door, u, camX, camY);
    const rx = (r.right - r.left) / 2;
    const ry = (r.bottom - r.top) / 2;
    const dx = (sx - (r.left + r.right) / 2) / rx;
    const dy = (sy - (r.top + r.bottom) / 2) / ry;
    const score = dx * dx + dy * dy;
    if (score > 1) continue;
    if (!best || score < best.score || (score === best.score && door.id < best.id)) {
      best = { x: door.x, y: door.y, id: door.id, score };
    }
  }
  return best ? { x: best.x, y: best.y } : null;
}

/** The sprite family's frames (the atlas entry keyed like the Renderer keys
 *  it — kind carries the tint, the atlas is keyed by the sprite family), or
 *  null when the atlas has no frames for the family. */
function familyFrames(assets: HeroAssets | null, kind: string): { dims: [number, number]; anchor: [number, number] } | null {
  const npc = assets?.atlas.npcs?.[enemyByKind(kind)?.family ?? kind];
  return npc ?? null;
}

/** The screen-space rect of an enemy's rendered sprite billboard, at its
 *  RENDER position (the interpolated position the Viewport draws) — the feet
 *  anchor at the tile diamond centre, the frame rect rising above it exactly
 *  as drawEnemySprite drew it (atlas dims × u, anchored by the atlas anchor ×
 *  u), with the inset applied. Null when the atlas has no frames for the
 *  family (nothing to click — the caller falls back to tile resolution). */
export function enemySpriteRect(
  assets: HeroAssets | null,
  e: ObservedEnemy,
  pos: Vec2,
  u: number,
  camX: number,
  camY: number,
  inset = ENEMY_HIT_INSET,
): Rect | null {
  const f = familyFrames(assets, e.kind);
  if (!f) return null;
  return spriteRect(f.dims, f.anchor, pos, u, camX, camY, inset);
}

/**
 * The live enemy whose sprite body contains the screen point, or null. Renders
 * at the interpolated position (`positions` — the Viewport's latest frame;
 * falls back to the authoritative tile before the first frame); dead enemies
 * have no sprite (the bones decal is a ground tile) and are never hit here.
 * Exact body hits beat margin hits. When several bodies overlap, the PAINTER-FRONTMOST wins (the one drawn last
 * is the one you see — the same painterOrder the Renderer sorted by, at the
 * rounded render tile); painter ties (stacked stackables share a tile) break
 * to the lowest id, the arena/actions resolver's existing deterministic rule.
 * Missing atlas / unknown kind ⇒ null (tile resolution unchanged).
 */
export function enemyAtScreenPoint(
  assets: HeroAssets | null,
  obs: Observation,
  positions: ReadonlyMap<string, Vec2>,
  sx: number,
  sy: number,
  u: number,
  camX: number,
  camY: number,
): ObservedEnemy | null {
  return enemyHitsAtScreenPoint(assets, obs, positions, sx, sy, u, camX, camY)[0] ?? null;
}

/** Every live enemy whose rendered body contains the screen point, sorted in
 * painter order from frontmost to backmost. Right-click menus use this list;
 * the old single-result helper remains the left-click frontmost projection. */
export function enemyHitsAtScreenPoint(
  assets: HeroAssets | null,
  obs: Observation,
  positions: ReadonlyMap<string, Vec2>,
  sx: number,
  sy: number,
  u: number,
  camX: number,
  camY: number,
): ObservedEnemy[] {
  const exact: { enemy: ObservedEnemy; pos: Vec2 }[] = [];
  const nearby: { enemy: ObservedEnemy; pos: Vec2 }[] = [];
  for (const e of obs.enemies) {
    if (!e.alive) continue;
    const pos = positions.get(enemyKey(e.id)) ?? { x: e.x, y: e.y };
    const r = enemySpriteRect(assets, e, pos, u, camX, camY);
    if (!r) continue;
    if (contains(r, sx, sy)) exact.push({ enemy: e, pos });
    else if (containsWithMargin(r, sx, sy, BODY_CLICK_MARGIN)) nearby.push({ enemy: e, pos });
  }
  const hits = exact.length > 0 ? exact : nearby;
  hits.sort((a, b) => {
    const order = painterOrder(
      { x: Math.round(b.pos.x), y: Math.round(b.pos.y), kind: "entity" },
      { x: Math.round(a.pos.x), y: Math.round(a.pos.y), kind: "entity" },
    );
    return order !== 0 ? order : a.enemy.id - b.enemy.id;
  });
  return hits.map((h) => h.enemy);
}

/** Bodies whose full billboard frame is near a point, used only to prevent a
 * transparent gap between adjacent NPCs from falling through to one tile's
 * arbitrary menu. Visible-body hits remain the authoritative left-click and
 * single-target right-click projection; these broad candidates turn an
 * ambiguous gap into an all-target menu (or a ground menu for one frame).
 */
export function enemyCandidatesAtScreenPoint(
  assets: HeroAssets | null,
  obs: Observation,
  positions: ReadonlyMap<string, Vec2>,
  sx: number,
  sy: number,
  u: number,
  camX: number,
  camY: number,
): ObservedEnemy[] {
  const candidates: { enemy: ObservedEnemy; pos: Vec2 }[] = [];
  for (const e of obs.enemies) {
    if (!e.alive) continue;
    const pos = positions.get(enemyKey(e.id)) ?? { x: e.x, y: e.y };
    const r = enemySpriteRect(assets, e, pos, u, camX, camY, 0);
    if (!r || !frameContains(r, sx, sy)) continue;
    candidates.push({ enemy: e, pos });
  }
  candidates.sort((a, b) => {
    const order = painterOrder(
      { x: Math.round(b.pos.x), y: Math.round(b.pos.y), kind: "entity" },
      { x: Math.round(a.pos.x), y: Math.round(a.pos.y), kind: "entity" },
    );
    return order !== 0 ? order : a.enemy.id - b.enemy.id;
  });
  return candidates.map((h) => h.enemy);
}

/** Hub counterpart of enemyHitsAtScreenPoint. Players use the hero atlas
 * billboard and are returned front-to-back so a torso click remains tied to
 * the visible character even when its feet project over another tile. */
export function playerHitsAtScreenPoint(
  assets: HeroAssets | null,
  obs: Observation,
  positions: ReadonlyMap<string, Vec2>,
  sx: number,
  sy: number,
  u: number,
  camX: number,
  camY: number,
  localPlayerId = -1,
): ObservedPlayer[] {
  const hero = assets?.atlas.sprites.hero;
  if (!hero) return [];
  const exact: { player: ObservedPlayer; pos: Vec2 }[] = [];
  const nearby: { player: ObservedPlayer; pos: Vec2 }[] = [];
  for (const p of obs.players) {
    if (!p.alive) continue;
    const pos = positions.get(playerKey(p.id)) ?? { x: p.x, y: p.y };
    const rect = spriteRect(hero.dims, hero.anchor, pos, u, camX, camY);
    if (spriteRectContains(rect, sx, sy)) exact.push({ player: p, pos });
    else if (p.id !== localPlayerId && containsWithMargin(rect, sx, sy, BODY_CLICK_MARGIN)) nearby.push({ player: p, pos });
  }
  const hits = exact.length > 0 ? exact : nearby;
  hits.sort((a, b) => {
    const order = painterOrder(
      { x: Math.round(b.pos.x), y: Math.round(b.pos.y), kind: "entity" },
      { x: Math.round(a.pos.x), y: Math.round(a.pos.y), kind: "entity" },
    );
    return order !== 0 ? order : a.player.id - b.player.id;
  });
  return hits.map((h) => h.player);
}

/** Full-frame player candidates for the hub's transparent-gap menu rule. */
export function playerCandidatesAtScreenPoint(
  assets: HeroAssets | null,
  obs: Observation,
  positions: ReadonlyMap<string, Vec2>,
  sx: number,
  sy: number,
  u: number,
  camX: number,
  camY: number,
): ObservedPlayer[] {
  const hero = assets?.atlas.sprites.hero;
  if (!hero) return [];
  const candidates: { player: ObservedPlayer; pos: Vec2 }[] = [];
  for (const p of obs.players) {
    if (!p.alive) continue;
    const pos = positions.get(playerKey(p.id)) ?? { x: p.x, y: p.y };
    const rect = spriteRect(hero.dims, hero.anchor, pos, u, camX, camY, 0);
    if (frameContains(rect, sx, sy)) candidates.push({ player: p, pos });
  }
  candidates.sort((a, b) => {
    const order = painterOrder(
      { x: Math.round(b.pos.x), y: Math.round(b.pos.y), kind: "entity" },
      { x: Math.round(a.pos.x), y: Math.round(a.pos.y), kind: "entity" },
    );
    return order !== 0 ? order : a.player.id - b.player.id;
  });
  return candidates.map((h) => h.player);
}
